/* eslint-disable indent */
import React, { useContext, useState } from "react";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { Beer, Pencil, Scroll, Store, Users, X } from "lucide-react";
import AddAmenityDialog from "@/components/molecules/TavernManagement/AddAmenityDialog";
import AddIntrigueDialog from "@/components/molecules/TavernManagement/AddIntrigueDialog";
import AddPatronDialog from "@/components/molecules/TavernManagement/AddPatronDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AppContext } from "@/lib/contexts";
import { AppReducerActionType, EditTavernRequest, Patron, TavernGenerationResponse, UserPremiumType } from "@/types";
interface TavernViewProps {
  tavern: TavernGenerationResponse;
  onEdit: (id: string, data: EditTavernRequest) => void;
}
const TavernView: React.FC<TavernViewProps> = ({
  tavern,
  onEdit
}) => {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPatronIndex, setEditingPatronIndex] = useState<number | null>(null);
  const [editingIntrigueIndex, setEditingIntrigueIndex] = useState<number | null>(null);
  const [editData, setEditData] = useState<EditTavernRequest>({
    tavernName: tavern.outputData.tavernName,
    tavernkeeper: tavern.outputData.tavernkeeper,
    amenities: tavern.outputData.amenities,
    quirks: tavern.outputData.quirks,
    intrigue: tavern.outputData.intrigue,
    plotHooks: tavern.outputData.plotHooks,
    patrons: tavern.outputData.patrons,
    history: tavern.outputData.history,
    lore: tavern.outputData.lore
  });
  const handleSave = async () => {
    // Check if user is logged in
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    if (state.userPremiumLevel === UserPremiumType.NONE || state.userPremiumLevel === UserPremiumType.BASIC) {
      toast.error("Editing Taverns is only available for Plus, Elite, or Premium Subscribers.");
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    try {
      await onEdit(tavern.id, editData);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating tavern:", error);
      toast.error("Failed to update tavern. Please try again.");
    }
  };
  const handleCancel = () => {
    setEditData({
      tavernName: tavern.outputData.tavernName,
      tavernkeeper: tavern.outputData.tavernkeeper,
      amenities: tavern.outputData.amenities,
      quirks: tavern.outputData.quirks,
      intrigue: tavern.outputData.intrigue,
      plotHooks: tavern.outputData.plotHooks,
      patrons: tavern.outputData.patrons,
      history: tavern.outputData.history,
      lore: tavern.outputData.lore
    });
    setIsEditing(false);
  };
  const handleAddAmenity = async (amenity: any) => {
    const newData = {
      ...editData,
      amenities: [...editData.amenities, amenity]
    };
    await onEdit(tavern.id, newData);
  };
  const handleAddPatron = async (patron: any) => {
    const newData = {
      ...editData,
      patrons: [...editData.patrons, patron]
    };
    await onEdit(tavern.id, newData);
  };
  const handleEditPatron = async (index: number, updatedPatron: Patron) => {
    const newPatrons = [...editData.patrons];
    newPatrons[index] = updatedPatron;
    const newData = {
      ...editData,
      patrons: newPatrons
    };
    await onEdit(tavern.id, newData);
    setEditingPatronIndex(null);
  };
  const handleRemovePatron = async (index: number) => {
    const newPatrons = editData.patrons.filter((_, i) => i !== index);
    const newData = {
      ...editData,
      patrons: newPatrons
    };
    await onEdit(tavern.id, newData);
  };
  const handleAddIntrigue = async (intrigue: string) => {
    const intrigues = editData.intrigue.split("\n\n").filter(Boolean);
    intrigues.push(intrigue);
    const newData = {
      ...editData,
      intrigue: intrigues.join("\n\n")
    };
    await onEdit(tavern.id, newData);
  };
  const handleEditIntrigue = async (index: number, updatedIntrigue: string) => {
    const intrigues = editData.intrigue.split("\n\n").filter(Boolean);
    intrigues[index] = updatedIntrigue;
    const newData = {
      ...editData,
      intrigue: intrigues.join("\n\n")
    };
    await onEdit(tavern.id, newData);
    setEditingIntrigueIndex(null);
  };
  const handleRemoveIntrigue = async (index: number) => {
    const intrigues = editData.intrigue.split("\n\n").filter(Boolean);
    intrigues.splice(index, 1);
    const newData = {
      ...editData,
      intrigue: intrigues.join("\n\n")
    };
    await onEdit(tavern.id, newData);
  };
  return <div className="space-y-8 w-full" data-sentry-component="TavernView" data-sentry-source-file="index.tsx">
            {/* Tavern Header */}
            <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <Store className="w-8 h-8 text-sky-400" data-sentry-element="Store" data-sentry-source-file="index.tsx" />
                        {isEditing ? <Input value={editData.tavernName} onChange={e => setEditData({
            ...editData,
            tavernName: e.target.value
          })} className="text-xl sm:text-3xl font-bold h-auto py-1" /> : <h1 className="text-xl sm:text-3xl font-bold text-slate-50">
                                {tavern.outputData.tavernName}
                            </h1>}
                    </div>
                    <div className="flex items-center gap-2">
                        {isEditing ? <>
                                <Button variant="ghost" size="sm" onClick={handleCancel} className="w-full sm:w-auto">
                                    Cancel
                                </Button>
                                <Button size="sm" onClick={handleSave} className="w-full sm:w-auto">
                                    Save Changes
                                </Button>
                            </> : <>
                                <Button variant="outline" size="sm" className="border-slate-700" asChild>
                                    <a href={`/tavern/${tavern.id}`}>
                                        View Full Page
                                    </a>
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)} className="w-full sm:w-auto">
                                    <Pencil className="w-4 h-4 mr-2" />
                                    Edit Tavern
                                </Button>
                            </>}
                    </div>
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                    <span>
                        Created on{" "}
                        {new Date(tavern.createdAt).toLocaleDateString()}
                    </span>
                </div>
            </div>

            {/* Tavernkeeper */}
            <div className="bg-sky-400/10 rounded-lg p-4 sm:p-6 border border-slate-700">
                <h2 className="text-lg sm:text-xl font-semibold text-sky-400 mb-4">
                    Tavernkeeper
                </h2>
                <div className="space-y-4">
                    {isEditing ? <>
                            <div>
                                <Label>Name</Label>
                                <Input value={editData.tavernkeeper.name} onChange={e => setEditData({
              ...editData,
              tavernkeeper: {
                ...editData.tavernkeeper,
                name: e.target.value
              }
            })} />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div>
                                    <Label>Level</Label>
                                    <Input type="number" value={editData.tavernkeeper.level} onChange={e => setEditData({
                ...editData,
                tavernkeeper: {
                  ...editData.tavernkeeper,
                  level: parseInt(e.target.value)
                }
              })} />
                                </div>
                                <div>
                                    <Label>Race</Label>
                                    <Input value={editData.tavernkeeper.race} onChange={e => setEditData({
                ...editData,
                tavernkeeper: {
                  ...editData.tavernkeeper,
                  race: e.target.value
                }
              })} />
                                </div>
                                <div>
                                    <Label>Class</Label>
                                    <Input value={editData.tavernkeeper.class} onChange={e => setEditData({
                ...editData,
                tavernkeeper: {
                  ...editData.tavernkeeper,
                  class: e.target.value
                }
              })} />
                                </div>
                            </div>
                        </> : <>
                            <p className="text-base sm:text-lg font-medium text-slate-200">
                                {tavern.outputData.tavernkeeper.name}
                            </p>
                            <p className="text-sm sm:text-base text-slate-400">
                                Level {tavern.outputData.tavernkeeper.level}{" "}
                                {tavern.outputData.tavernkeeper.race}{" "}
                                {tavern.outputData.tavernkeeper.class}
                            </p>
                        </>}
                </div>
            </div>

            {/* Amenities */}
            <div className="space-y-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <h2 className="text-lg sm:text-xl font-semibold text-slate-50">
                        Amenities
                    </h2>
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <div className="flex items-center gap-2 text-slate-400">
                            <Beer className="w-4 h-4" data-sentry-element="Beer" data-sentry-source-file="index.tsx" />
                            <span>
                                {tavern.outputData.amenities.length} amenities
                            </span>
                        </div>
                        {isEditing && <AddAmenityDialog onAdd={handleAddAmenity} />}
                    </div>
                </div>

                <div className="grid gap-4">
                    {tavern.outputData.amenities.map((amenity, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                            <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                                <div className="flex items-start gap-3 w-full sm:w-auto">
                                    <div className="mt-1">
                                        <Beer className="w-5 h-5 text-sky-400" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-medium text-slate-200">
                                            {amenity.name}
                                        </h3>
                                        <p className="text-sm text-slate-400 mt-1">
                                            {amenity.description}
                                        </p>
                                        <span className="text-sm text-sky-400">
                                            {amenity.type}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </motion.div>)}
                </div>
            </div>

            {/* Patrons */}
            <div className="space-y-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <h2 className="text-lg sm:text-xl font-semibold text-slate-50">
                        Patrons
                    </h2>
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <div className="flex items-center gap-2 text-slate-400">
                            <Users className="w-4 h-4" data-sentry-element="Users" data-sentry-source-file="index.tsx" />
                            <span>
                                {tavern.outputData.patrons.length} patrons
                            </span>
                        </div>
                        {isEditing && <AddPatronDialog onAdd={handleAddPatron} />}
                    </div>
                </div>

                <div className="grid gap-4">
                    {editData.patrons.map((patron, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                            {editingPatronIndex === index ? <AddPatronDialog initialData={editData.patrons[index]} onAdd={updatedPatron => handleEditPatron(index, updatedPatron)} onCancel={() => setEditingPatronIndex(null)} /> : <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-slate-100 mb-1">
                                            {patron.name}
                                        </h3>
                                        <p className="text-slate-200 mb-1">
                                            {patron.personality}
                                        </p>
                                        <span className="text-sm text-sky-400">
                                            {patron.role}
                                        </span>
                                    </div>
                                    {isEditing && <div className="flex items-center gap-1">
                                            <Button variant="ghost" size="icon" className="text-sky-400 hover:text-sky-300" onClick={() => setEditingPatronIndex(index)}>
                                                <Pencil className="w-4 h-4" />
                                            </Button>
                                            <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-300" onClick={() => handleRemovePatron(index)}>
                                                <X className="w-4 h-4" />
                                            </Button>
                                        </div>}
                                </div>}
                        </motion.div>)}
                </div>
            </div>

            {/* Intrigue */}
            <div className="space-y-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <h2 className="text-lg sm:text-xl font-semibold text-slate-50">
                        Intrigue
                    </h2>
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <div className="flex items-center gap-2 text-slate-400">
                            <Scroll className="w-4 h-4" data-sentry-element="Scroll" data-sentry-source-file="index.tsx" />
                            <span>
                                {editData.intrigue.split("\n\n").filter(Boolean).length}{" "}
                                plots
                            </span>
                        </div>
                        {isEditing && <AddIntrigueDialog onAdd={handleAddIntrigue} />}
                    </div>
                </div>

                <div className="grid gap-4">
                    {editData.intrigue.split("\n\n").filter(Boolean).map((intrigue, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                {editingIntrigueIndex === index ? <AddIntrigueDialog initialData={editData.intrigue.split("\n\n").filter(Boolean)[index]} onAdd={updatedIntrigue => handleEditIntrigue(index, updatedIntrigue)} onCancel={() => setEditingIntrigueIndex(null)} /> : <div className="flex items-start justify-between gap-4">
                                        <p className="text-slate-200 flex-1">
                                            {intrigue}
                                        </p>
                                        {isEditing && <div className="flex items-center gap-1">
                                                <Button variant="ghost" size="icon" className="text-sky-400 hover:text-sky-300" onClick={() => setEditingIntrigueIndex(index)}>
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-300" onClick={() => handleRemoveIntrigue(index)}>
                                                    <X className="w-4 h-4" />
                                                </Button>
                                            </div>}
                                    </div>}
                            </motion.div>)}
                </div>
            </div>

            {/* Additional Information */}
            <div className="space-y-4">
                {/* Quirks */}
                <div className="bg-emerald-400/10 rounded-lg p-4 sm:p-6 border border-emerald-950">
                    <h2 className="text-lg sm:text-xl font-semibold text-emerald-400 mb-3">
                        Quirks
                    </h2>
                    {isEditing ? <Textarea value={editData.quirks} onChange={e => setEditData({
          ...editData,
          quirks: e.target.value
        })} className="min-h-[100px]" /> : <p className="text-sm sm:text-base text-slate-200">
                            {tavern.outputData.quirks}
                        </p>}
                </div>

                {/* History & Lore */}
                <div className="bg-sky-400/10 rounded-lg p-4 sm:p-6 border border-slate-700">
                    <h2 className="text-lg sm:text-xl font-semibold text-sky-400 mb-4">
                        History & Lore
                    </h2>
                    <div className="space-y-4">
                        <div>
                            <h3 className="text-lg font-semibold text-slate-100 mb-2">
                                History
                            </h3>
                            {isEditing ? <Textarea value={editData.history} onChange={e => setEditData({
              ...editData,
              history: e.target.value
            })} className="min-h-[100px]" /> : <p className="text-slate-200 leading-relaxed">
                                    {tavern.outputData.history}
                                </p>}
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-slate-100 mb-2">
                                Lore
                            </h3>
                            {isEditing ? <Textarea value={editData.lore} onChange={e => setEditData({
              ...editData,
              lore: e.target.value
            })} className="min-h-[100px]" /> : <p className="text-slate-200 leading-relaxed">
                                    {tavern.outputData.lore}
                                </p>}
                        </div>
                    </div>
                </div>
            </div>
        </div>;
};
export default TavernView;